import { useTranslation } from 'react-i18next';
import { Container, Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Section, AnimatedBox } from '../../components/ui';

const PageHeader = styled(Box)(({ theme }) => ({
  background: theme.palette.background.hero,
  padding: theme.spacing(16, 0, 8),
  position: 'relative',

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(12, 0, 6),
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),

  '& h2': {
    fontSize: '1.5rem',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },

  '& p': {
    marginBottom: theme.spacing(2),
    lineHeight: 1.8,
    color: theme.palette.text.secondary,
  },
}));

export default function Terms() {
  const { t } = useTranslation();
  const lastUpdated = 'January 2024';

  return (
    <>
      <PageHeader>
        <Container maxWidth="lg">
          <AnimatedBox animation="fadeInUp">
            <Typography variant="h1" sx={{ color: 'text.light', mb: 2 }}>
              Terms of Service
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Last updated: {lastUpdated}
            </Typography>
          </AnimatedBox>
        </Container>
      </PageHeader>

      <Section>
        <Container maxWidth="md">
          <AnimatedBox animation="fadeInUp">
            <ContentSection>
              <Typography variant="h2" component="h2">1. Agreement to Terms</Typography>
              <Typography component="p">
                These Terms of Service constitute a legally binding agreement made between you and Nexfah ("Company," "we," "us," or "our"), concerning your access to and use of our website and services.
              </Typography>
              <Typography component="p">
                By accessing or using our services, you agree that you have read, understood, and agree to be bound by all of these Terms of Service. If you do not agree with all of these terms, you are expressly prohibited from using our services.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">2. Services</Typography>
              <Typography component="p">
                Nexfah provides digital transformation and software development services, including but not limited to:
              </Typography>
              <List>
                <ListItem><ListItemText primary="Business process digitization and automation" /></ListItem>
                <ListItem><ListItemText primary="Custom software and application development" /></ListItem>
                <ListItem><ListItemText primary="Enterprise software engineering" /></ListItem>
                <ListItem><ListItemText primary="IT consulting and advisory services" /></ListItem>
                <ListItem><ListItemText primary="IoT solutions and implementations" /></ListItem>
              </List>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">3. Intellectual Property Rights</Typography>
              <Typography component="p">
                Unless otherwise indicated, the Site and all content, features, and functionality (including but not limited to all information, software, text, displays, images, video, and audio) are owned by Nexfah and are protected by copyright, trademark, patent, trade secret, and other intellectual property laws.
              </Typography>
              <Typography component="p">
                You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any materials from our site without prior written consent.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">4. User Representations</Typography>
              <Typography component="p">
                By using our services, you represent and warrant that:
              </Typography>
              <List>
                <ListItem><ListItemText primary="You have the legal capacity to enter into these Terms of Service" /></ListItem>
                <ListItem><ListItemText primary="You are not a minor in the jurisdiction in which you reside" /></ListItem>
                <ListItem><ListItemText primary="You will not use the services for any illegal or unauthorized purpose" /></ListItem>
                <ListItem><ListItemText primary="Your use will not violate any applicable law or regulation" /></ListItem>
                <ListItem><ListItemText primary="All information you provide is true, accurate, current, and complete" /></ListItem>
              </List>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">5. Payment Terms</Typography>
              <Typography component="p">
                For paid services, you agree to provide current, complete, and accurate billing information. You agree to promptly update your account and payment information to keep it accurate and complete.
              </Typography>
              <Typography component="p">
                Payment terms will be specified in individual service agreements or proposals. All fees are non-refundable unless otherwise specified in writing.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">6. Project Delivery</Typography>
              <Typography component="p">
                Project timelines, deliverables, and specifications will be outlined in individual project agreements. We will make reasonable efforts to meet agreed-upon deadlines, but delays may occur due to factors beyond our control.
              </Typography>
              <Typography component="p">
                Changes to project scope may affect timelines and costs. Any changes must be agreed upon in writing by both parties.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">7. Confidentiality</Typography>
              <Typography component="p">
                Both parties agree to maintain the confidentiality of any proprietary or confidential information disclosed during the course of the business relationship. This includes technical data, business strategies, customer information, and any other sensitive materials.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">8. Limitation of Liability</Typography>
              <Typography component="p">
                To the maximum extent permitted by law, Nexfah shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses.
              </Typography>
              <Typography component="p">
                Our total liability for any claims under these terms shall not exceed the amount paid by you to us during the twelve (12) months prior to the claim.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">9. Indemnification</Typography>
              <Typography component="p">
                You agree to defend, indemnify, and hold harmless Nexfah and its officers, directors, employees, and agents from any claims, damages, obligations, losses, liabilities, costs, or debt arising from your use of our services or violation of these Terms.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">10. Termination</Typography>
              <Typography component="p">
                We may terminate or suspend your access to our services immediately, without prior notice, for any reason whatsoever, including breach of these Terms. Upon termination, your right to use the services will cease immediately.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">11. Governing Law</Typography>
              <Typography component="p">
                These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law provisions. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">12. Changes to Terms</Typography>
              <Typography component="p">
                We reserve the right to modify or replace these Terms at any time at our sole discretion. We will provide notice of any material changes by posting the new Terms on this page and updating the "Last updated" date.
              </Typography>
            </ContentSection>

            <ContentSection>
              <Typography variant="h2" component="h2">13. Contact Information</Typography>
              <Typography component="p">
                For questions about these Terms of Service, please contact us at:
              </Typography>
              <Typography component="p">
                <strong>Nexfah</strong><br />
                Email: info@nexfah.com<br />
                Phone: +44 7888 601455<br />
                London, United Kingdom
              </Typography>
            </ContentSection>
          </AnimatedBox>
        </Container>
      </Section>
    </>
  );
}
